/* Softphone — the agent's telephony surface inside Agent Desk.
 *
 * Two operating modes, picked at runtime by useTelnyxClient:
 *   - "live"      — SIP creds are configured; @telnyx/webrtc has
 *                   registered. Incoming/Accept/Decline/Hangup buttons
 *                   drive the real Call object.
 *   - "simulated" — worker returned 503 (no SIP creds in dev). The
 *                   "Simulate ring" button still works so the rest of
 *                   the desk can be exercised without telephony.
 *
 * Heartbeat (/api/agents/heartbeat) and sign-out (/api/agents/sign-out)
 * stay client-driven regardless of mode — that's what powers the bot's
 * TeXML branch decision (bridge inbound call to the agent SIP URI when
 * ≥1 agent is online). */

import { useEffect, useRef, useState } from "react";

import { API_URL } from "../lib/api";
import { CallControls } from "./CallControls";
import { agentHeaders, useAgentIdentity } from "./identity";
import { useTelnyxClient, type SoftphoneMode } from "./useTelnyxClient";

type SoftphoneStatus = "signed-out" | "available" | "away" | "incoming" | "on-call";

const HEARTBEAT_MS = 15_000;

const STATUS_LABEL: Record<SoftphoneStatus, string> = {
  "signed-out": "Signed out",
  "available":  "Available",
  "away":       "Away",
  "incoming":   "Incoming…",
  "on-call":    "On call",
};

export function Softphone() {
  const { identity } = useAgentIdentity();
  const [status, setStatus] = useState<SoftphoneStatus>(() =>
    // Lazy init from previous session — the agent's last status is
    // remembered across reloads so they don't get rung unless they
    // explicitly came back as Available.
    readSavedStatus(),
  );
  const [onlineCount, setOnlineCount] = useState<number | null>(null);
  const [onlineNames, setOnlineNames] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);

  // Connect the SDK only while the agent is signed in and not signed
  // out (away keeps the SDK live so they can come back without
  // re-registering). The hook reports mode === "simulated" when the
  // agent's row has no SIP credential, in which case the local
  // "Simulate ring" path stays available.
  const sdkAgentId =
    identity !== null && status !== "signed-out" ? identity.agentId : null;
  const sdk = useTelnyxClient(sdkAgentId);

  // SDK → status reflection. The hook reports the real Call objects;
  // mirror those into the local SoftphoneStatus enum so the UI doesn't
  // have to care which mode we're in.
  useEffect(() => {
    if (sdk.mode !== "live") return;
    if (sdk.currentCall) {
      setStatus("on-call");
    } else if (sdk.incomingCall) {
      setStatus("incoming");
    } else if (status === "incoming" || status === "on-call") {
      // Real call ended — drop back to available rather than stay on a
      // stale "Incoming…" / "On call" label.
      setStatus("available");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdk.mode, sdk.incomingCall, sdk.currentCall]);

  // Surface SDK errors in the same place as heartbeat errors.
  useEffect(() => {
    if (sdk.error && sdk.mode !== "simulated") setErr(sdk.error);
  }, [sdk.error, sdk.mode]);

  // Drop to signed-out when identity goes away.
  useEffect(() => {
    if (identity === null && status !== "signed-out") {
      setStatus("signed-out");
    }
    // When identity arrives and we were signed-out, default to Available.
    if (identity !== null && status === "signed-out") {
      setStatus("available");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  // Persist status across reloads.
  useEffect(() => {
    saveStatus(status);
  }, [status]);

  // Heartbeat loop — runs while we have an identity AND we're not in
  // signed-out. The worker uses this to know who's at the desk.
  // Skip when this tab has been kicked by a newer tab (duplicate-tab):
  // posting heartbeats would have us claim the agent_id while another
  // tab actually holds the live Verto session, leading the bot to
  // bridge to whichever Verto registration Telnyx happens to pick.
  useEffect(() => {
    if (identity === null) return;
    if (status === "signed-out") return;
    if (sdk.mode === "duplicate-tab") return;

    let cancelled = false;
    async function ping(s: SoftphoneStatus) {
      const headers: Record<string, string> = {
        "content-type": "application/json",
        ...agentHeaders(identity),
      };
      try {
        const r = await fetch(`${API_URL}/api/agents/heartbeat`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            status: effectiveHeartbeatStatus(s, sdk.mode, sdk.connected),
          }),
        });
        if (!r.ok) {
          if (!cancelled) setErr(`heartbeat ${r.status}`);
          return;
        }
        const data = (await r.json()) as { online?: number; names?: string[] };
        if (!cancelled && typeof data.online === "number") {
          setOnlineCount(data.online);
          setOnlineNames(Array.isArray(data.names) ? data.names : []);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : String(e));
        }
      }
    }

    void ping(status);
    const id = setInterval(() => void ping(status), HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // sdk.mode + sdk.connected are intentional deps: when the Verto
    // session toggles registered/disconnected (or this tab gets kicked
    // into duplicate-tab) we want the next heartbeat (and the immediate
    // ping on this rerun) to reflect that promptly — a freshly-stale
    // agent stops claiming "available" within a tick rather than the
    // full HEARTBEAT_MS interval.
  }, [identity, status, sdk.mode, sdk.connected]);

  // Sign-out hook: when identity clears (the SignInModal flow's "Sign
  // out" button fires identity.clear()), tell the worker so we don't
  // sit around as stale-but-online for 30s. Run once per identity.
  const lastIdRef = useRef<number | null>(null);
  useEffect(() => {
    const cur = identity?.agentId ?? null;
    if (cur === null && lastIdRef.current !== null) {
      // We just transitioned identity → null. Best-effort wipe.
      const agentId = lastIdRef.current;
      void fetch(`${API_URL}/api/agents/sign-out`, {
        method: "POST",
        headers: { "X-Agent-Id": String(agentId) },
      }).catch(() => {});
    }
    lastIdRef.current = cur;
  }, [identity]);

  if (!identity) return null;

  const isLive = sdk.mode === "live";
  // Duplicate-tab — another tab/window has agent-desk open with the
  // same identity. We've torn down our SDK in useTelnyxClient (last
  // tab to mount wins). UI is read-only until refresh; user should
  // either close this tab or refresh to claim ownership.
  const duplicateTab = sdk.mode === "duplicate-tab";
  // "Not ready" — Verto isn't registered with Telnyx yet (or has
  // disconnected). The agent's chosen status is preserved (so flipping
  // back to "available" works the moment we reconnect), but the
  // displayed label and the heartbeat both reflect "not really here"
  // until telnyx.ready fires. Without this, an agent could claim
  // "Available" with a dead Verto session and silently miss every
  // INVITE while the bot dropped calls into AI fallback.
  //
  // Exception: a live incoming/on-call is itself proof the SDK is
  // delivering INVITEs and bridging audio — never downgrade to "Not
  // ready" / "Connecting…" while a call is on the wire. This also
  // guards against the visibility-change handler in useTelnyxClient
  // briefly setting connected=false on tab return (mid-token-refresh
  // or just-after-resume), which would otherwise hide the Incoming…
  // label and the Accept button while the phone audibly rings.
  const hasLiveCall = sdk.incomingCall !== null || sdk.currentCall !== null;
  const notReady =
    !duplicateTab && !hasLiveCall &&
    ((sdk.mode === "live" && !sdk.connected) || sdk.mode === "error");
  const displayLabel = duplicateTab
    ? "Open in another tab"
    : notReady
      ? sdk.mode === "error"
        ? "Not ready"
        : "Connecting…"
      : STATUS_LABEL[status];

  return (
    <section
      id="agent-desk-softphone"
      className={`agent-desk-softphone is-${status} mode-${sdk.mode}${notReady ? " is-not-ready" : ""}${duplicateTab ? " is-duplicate-tab" : ""}`}
      aria-label="Softphone"
    >
      <div className="agent-desk-softphone-status">
        <span className="agent-desk-softphone-dot" aria-hidden />
        <span className="agent-desk-softphone-label">{displayLabel}</span>
        {onlineCount !== null && !notReady && !duplicateTab && (
          <span
            className="agent-desk-softphone-online"
            title={
              onlineNames.length > 0
                ? `Online: ${onlineNames.join(", ")}`
                : "Agents currently online"
            }
          >
            · {onlineCount} online
          </span>
        )}
        {sdk.mode === "simulated" && (
          <span
            className="agent-desk-softphone-mode"
            title="No SIP credentials configured; ring + answer are local-only"
          >
            · simulated
          </span>
        )}
        {duplicateTab && (
          <span
            className="agent-desk-softphone-not-ready"
            title="agent-desk is active in another tab/window for this account. Refresh this tab to use it here, or close it."
            role="status"
          >
            · refresh to use here, or close this tab
          </span>
        )}
        {notReady && (
          <span
            className="agent-desk-softphone-not-ready"
            title="Telnyx Verto session isn't registered — calls won't reach this softphone. Try refreshing the page."
            role="status"
          >
            · calls won't reach you · try refreshing
          </span>
        )}
      </div>

      <div className="agent-desk-softphone-actions">
        {duplicateTab && (
          <button
            id="agent-desk-softphone-refresh-btn"
            type="button"
            className="agent-desk-softphone-btn agent-desk-softphone-btn-primary"
            onClick={() => window.location.reload()}
            title="Reload this tab to claim the softphone here"
          >
            Refresh
          </button>
        )}
        {!duplicateTab && status === "available" && (
          <button
            id="agent-desk-softphone-away-btn"
            type="button"
            className="agent-desk-softphone-btn"
            onClick={() => setStatus("away")}
          >
            Set away
          </button>
        )}
        {!duplicateTab && status === "away" && (
          <button
            id="agent-desk-softphone-back-btn"
            type="button"
            className="agent-desk-softphone-btn agent-desk-softphone-btn-primary"
            onClick={() => setStatus("available")}
          >
            I'm back
          </button>
        )}
        {status === "incoming" && (
          <>
            <button
              id="agent-desk-softphone-accept-btn"
              type="button"
              className="agent-desk-softphone-btn agent-desk-softphone-btn-accept"
              onClick={() => {
                if (isLive) {
                  sdk.accept();
                } else {
                  setStatus("on-call");
                }
              }}
              title={isLive ? "Pick up" : "Pick up (simulated)"}
            >
              Accept
            </button>
            <button
              id="agent-desk-softphone-decline-btn"
              type="button"
              className="agent-desk-softphone-btn agent-desk-softphone-btn-decline"
              onClick={() => {
                if (isLive) {
                  sdk.decline();
                } else {
                  setStatus("available");
                }
              }}
            >
              Decline
            </button>
          </>
        )}
        {status === "on-call" && (
          <>
            {/* Mic mute / speaker mute / volume — only meaningful in
             *  live mode. The Call object the SDK exposes is what
             *  drives the outbound mic mute, so simulated mode skips
             *  these controls entirely (they'd be no-ops). */}
            {isLive && (
              <CallControls
                micMuted={sdk.micMuted}
                onMicToggle={sdk.setMicMuted}
              />
            )}
            <button
              id="agent-desk-softphone-hangup-btn"
              type="button"
              className="agent-desk-softphone-btn agent-desk-softphone-btn-decline"
              onClick={() => {
                if (isLive) {
                  sdk.hangup();
                } else {
                  setStatus("available");
                }
              }}
              title={isLive ? "Hang up" : "Hang up (simulated)"}
            >
              Hang up
            </button>
          </>
        )}
        {/* Local ring trigger — exposed only in simulated mode so the
         *  rest of the desk (transcript pane, profile rail, chat) can
         *  be exercised without SIP creds. In live mode the real SDK
         *  fires the incoming-call notification. */}
        {!isLive && status === "available" && (
          <button
            id="agent-desk-softphone-sim-incoming"
            type="button"
            className="agent-desk-softphone-btn agent-desk-softphone-btn-ghost"
            onClick={() => setStatus("incoming")}
            title="Simulate an incoming call (state-only — no audio)"
          >
            Simulate ring
          </button>
        )}
      </div>

      {err && <div className="agent-desk-softphone-err">{err}</div>}
    </section>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

const STATUS_KEY = "agent-desk:softphone-status";

function readSavedStatus(): SoftphoneStatus {
  try {
    const v = localStorage.getItem(STATUS_KEY);
    if (
      v === "available" ||
      v === "away" ||
      v === "signed-out"
    ) {
      return v;
    }
    // Don't restore transient states (incoming/on-call) — those should
    // never persist across reloads.
  } catch {
    /* fall through */
  }
  return "signed-out";
}

function saveStatus(s: SoftphoneStatus): void {
  try {
    // Only persist stable states.
    if (s === "available" || s === "away" || s === "signed-out") {
      localStorage.setItem(STATUS_KEY, s);
    }
  } catch {
    /* private mode */
  }
}

function heartbeatStatus(s: SoftphoneStatus): "available" | "busy" | "away" {
  if (s === "incoming" || s === "on-call") return "busy";
  if (s === "away") return "away";
  return "available";
}

// Effective heartbeat status: downgrade to "away" whenever the SDK isn't
// actually registered with Telnyx (Verto session live). Without this, a
// browser tab that finished sign-in but failed REGISTER would still
// claim "available" — the bot would pick that agent, try to bridge,
// and Telnyx would reply SIP 480 (no Verto session to deliver to),
// dropping the call into AI fallback. The agent would see no ring and
// no error. Now: not-registered ⇒ away ⇒ bot skips, picks the next
// agent or falls through cleanly.
function effectiveHeartbeatStatus(
  s: SoftphoneStatus,
  sdkMode: SoftphoneMode,
  connected: boolean,
): "available" | "busy" | "away" {
  if (sdkMode === "live" && !connected) return "away";
  if (sdkMode === "error") return "away";
  return heartbeatStatus(s);
}
